import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from 'react';
import { useAtom } from 'jotai';
import { useRouter } from 'next/router';

import { userInfoAtom } from '@/state/user/atom';

interface Step {
  target: string;
  content: string | ReactNode;
  autoClick?: boolean;
  disableBeacon?: boolean;
}

interface TourContextType {
  run: boolean;
  setRun: (value: boolean) => void;
  stepIndex: number;
  setStepIndex: (index: number) => void;
  steps: Step[];
  setCompleted: (value: boolean) => void;
}

const TourContext = createContext<TourContextType | undefined>(undefined);

export const TourProvider = ({ children }: { children: ReactNode }) => {
  const [run, setRun] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const [userInfo] = useAtom(userInfoAtom);
  const router = useRouter();

  const steps: Step[] = [
    {
      target: '.main',
      content: (
        <>
          세일즈클루에 오신걸 환영합니다! 🥳
          <br />
          간단히 사용법을 알려드릴게요.
        </>
      ),
      disableBeacon: true,
    },
    {
      target: '.pdf-upload-button',
      content: '여기서 PDF를 업로드할 수 있어요.',
      disableBeacon: true,
    },
    {
      target: '.slide-box',
      content: (
        <>
          업로드하면 여기에 표시돼요.
          <br />
          먼저 샘플 PDF를 한 번 살펴볼까요?
        </>
      ),
      autoClick: true,
      disableBeacon: true,
    },
    {
      target: '.share-link-button',
      content: (
        <>
          여기서 고객에게 공유할 수 있는
          <br /> 링크를 복사할 수 있어요.
        </>
      ),
      disableBeacon: true,
    },
    {
      target: '.pdf-tracking-clients',
      content: '여기서 열람한 고객 목록을 볼 수 있어요.',
      disableBeacon: true,
    },
    {
      target: '.pdf-tracking-client-list',
      content: (
        <>
          1:1 공유용 링크 또는 문의양식을
          <br /> 제출하지 않은 열람자는 {'"'}익명{'"'}으로 표시돼요.
          <br /> 나중에 고객 정보를 입력하면 익명 표시가 사라져요.
        </>
      ),
      disableBeacon: true,
    },
    {
      target: '.pdf-activity-analysis',
      content: (
        <>
          열람 분석에서 <br />
          고객 열람 세부 정보를 확인할 수 있어요.
        </>
      ),
      autoClick: true,
      disableBeacon: true,
    },
    {
      target: '.open-record-section',
      content: (
        <>
          여기서 고객이 열람한 기록을 <br />
          자세히 확인할 수 있어요.
        </>
      ),
      disableBeacon: true,
    },
    {
      target: '.pdf-share-setting',
      content: (
        <>
          여기서 PDF에 가림막, 문의양식, 버튼 추가 등을
          <br /> 설정할 수 있어요.
        </>
      ),
      disableBeacon: true,
    },
    {
      target: '.pdf-menu',
      content: '이제 PDF를 업로드하고 고객에게 공유해보세요! 🎉',
      disableBeacon: true,
    },
  ];

  useEffect(() => {
    if (router.pathname !== '/user/pdf' || completed) {
      return;
    }

    setStepIndex(0);
    setRun(userInfo?.tourStatus === 'NOT_STARTED');
  }, [userInfo, completed, router.pathname]);

  return (
    <TourContext.Provider
      value={{ run, setRun, stepIndex, setStepIndex, steps, setCompleted }}
    >
      {children}
    </TourContext.Provider>
  );
};

export const useTour = () => {
  const context = useContext(TourContext);
  if (!context) {
    throw new Error('useTour must be used within a TourProvider');
  }
  return context;
};
